import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import {
  useLinkPlatformChatKnowledgeBaseMutation,
  useUnlinkPlatformChatKnowledgeBaseMutation,
} from '../../features/chat/mutations/usePlatformChatMutations';
import { useCurrentUserProfileQuery } from '../../auth/queries/useCurrentUserProfileQuery';
import { useChatMessages } from '../../features/chat/chatMessages/useChatMessages';
import { usePlatformChatMessagesInfiniteQuery } from '../../features/chat/queries/usePlatformChatMessagesInfiniteQuery';
import { usePlatformChatLinkedKnowledgeBasesQuery } from '../../features/chat/queries/usePlatformChatLinkedKnowledgeBasesQuery';
import { useChatSessions } from '../../features/chat/chatSessions/useChatSessions';
import { usePlatformChatSessionQuery } from '../../features/chat/queries/usePlatformChatSessionQuery';
import { useKnowledgeDatabasesQuery } from '../../features/knowledge/queries/useKnowledgeDatabasesQuery';
import { useTenantUsersQuery } from '../../features/tenant/queries/useTenantUsersQuery';
import { LiksPage } from '../../components/liks/LiksPage/LiksPage';
import { useAutohideScrollbar } from '../../hooks/useAutohideScrollbar';
import { useLoadOlderChatMessages } from '../../hooks/useLoadOlderChatMessages';
import { useScrollToBottom } from '../../hooks/useScrollToBottom';
import { useI18n } from '../../i18n/useI18n';
import { useUserConfig } from '../../userConfig/useUserConfig';
import { resolveApiErrorMessage } from '../../query/apiErrorMessage';
import { toast } from '../../toast/toast';
import type { ChatMessageSendOptions } from '../../types/chatMessagesContext';
import { PlatformChatComposerArea } from './PlatformChatComposerArea';
import { PlatformChatMessageListHeader } from './PlatformChatMessageListHeader';
import { PlatformChatTranscript } from './PlatformChatTranscript';
import {
  buildDocumentPreviewKnowledgeBases,
  buildPlatformChatKnowledgeBaseOptions,
  resolveFallbackKnowledgeBaseId,
} from './platformChatPageHelpers';
import { usePlatformChatDisplayMessages } from './usePlatformChatDisplayMessages';
import { usePlatformChatScrollEffects } from './usePlatformChatScrollEffects';
import './PlatformChatPage.css';

export function PlatformChatPage() {
  const { chatId } = useParams();
  const { user } = useAuth();
  const { getSessionById } = useChatSessions();
  const { markPlatformChatSessionSeen } = useUserConfig();
  const sessionQuery = usePlatformChatSessionQuery(chatId);
  const { getOptimisticMessages, sendMessage, retryMessage, isSendingMessage } =
    useChatMessages();
  const knowledgeDatabasesQuery = useKnowledgeDatabasesQuery();
  const directoryUsersQuery = useTenantUsersQuery();
  const currentUserProfileQuery = useCurrentUserProfileQuery(Boolean(user));
  const linkedKnowledgeBasesQuery =
    usePlatformChatLinkedKnowledgeBasesQuery(chatId);
  const messagesQuery = usePlatformChatMessagesInfiniteQuery(chatId ?? '');
  const linkKnowledgeBaseMutation = useLinkPlatformChatKnowledgeBaseMutation();
  const unlinkKnowledgeBaseMutation =
    useUnlinkPlatformChatKnowledgeBaseMutation();
  const { locale, translate } = useI18n();
  const messagesScrollbar = useAutohideScrollbar();
  const {
    containerRef: messagesRef,
    isAtBottom,
    checkIsAtBottom,
    scrollToBottom,
  } = useScrollToBottom();
  const hasInitialScrollRef = useRef(false);
  const isLoadingOlderMessagesRef = useRef(false);

  const directoryUserById = useMemo(() => {
    const map = new Map(
      (directoryUsersQuery.data ?? []).map((directoryUser) => [
        directoryUser.id,
        directoryUser,
      ]),
    );
    return map;
  }, [directoryUsersQuery.data]);

  const normalizationContext = useMemo(
    () => ({
      sessionId: chatId ?? '',
      userId: user?.id,
      userDisplayName: user?.name ?? translate('common.youBadge'),
      defaultUserName: translate('layout.defaultUserName'),
      assistantName: translate('platformChat.assistantName'),
      directoryUserById,
    }),
    [chatId, directoryUserById, translate, user?.id, user?.name],
  );

  const hasNextPage = messagesQuery.hasNextPage ?? false;
  const isFetchingNextPage = messagesQuery.isFetchingNextPage;
  const rawMessages = useMemo(
    () => messagesQuery.data?.pages.flatMap((page) => page) ?? [],
    [messagesQuery.data?.pages],
  );

  const { messages, optimisticMessages, displayMessages, messageCount } =
    usePlatformChatDisplayMessages({
      chatId,
      normalizationContext,
      profileFirstName: currentUserProfileQuery.data?.firstName?.trim(),
      directoryFirstName:
        user?.id != null
          ? directoryUserById.get(user.id)?.firstName?.trim()
          : undefined,
      displayNameFirstName: user?.name?.trim().split(/\s+/)[0],
      rawMessages,
      getOptimisticMessages,
      hasNextPage,
      translateWelcome: (firstName) =>
        translate('platformChat.welcomeMessage', { firstName }),
      translateWelcomeNoName: translate('platformChat.welcomeMessageNoName'),
    });

  const isAwaitingAssistantReply =
    (chatId != null && isSendingMessage(chatId)) ||
    optimisticMessages.some(
      (message) =>
        message.isPendingAssistantReply === true ||
        message.isStreamingAssistantReply === true,
    );

  const displayMessageCount = displayMessages.length;
  const stickToBottom =
    isAtBottom || isAwaitingAssistantReply || optimisticMessages.length > 0;

  const linkedKnowledgeBaseByKnowledgeBaseId = useMemo(() => {
    const map = new Map<string, string>();
    for (const linkedKnowledgeBase of linkedKnowledgeBasesQuery.data ?? []) {
      map.set(linkedKnowledgeBase.knowledgeBaseId, linkedKnowledgeBase.id);
    }
    return map;
  }, [linkedKnowledgeBasesQuery.data]);

  const { handleScrollNearTop } = useLoadOlderChatMessages({
    containerRef: messagesRef,
    hasNextPage,
    isFetchingNextPage,
    isLoadingOlderMessagesRef,
    fetchNextPage: messagesQuery.fetchNextPage,
    contentVersion: messageCount,
    isReady: !messagesQuery.isLoading && messageCount > 0,
  });

  const handleMessagesScroll = useCallback(() => {
    messagesScrollbar.onScroll();
    checkIsAtBottom();
    handleScrollNearTop();
  }, [checkIsAtBottom, handleScrollNearTop, messagesScrollbar]);

  const handleSendMessage = async (
    content: string,
    options: ChatMessageSendOptions,
    attachmentIds: string[],
  ) => {
    if (!user || !chatId) {
      return;
    }

    await sendMessage(chatId, content, {
      ...options,
      attachmentIds,
    });
  };

  const handleKnowledgeBaseToggle = (knowledgeBaseId: string) => {
    if (!chatId) {
      return;
    }

    const linkedId = linkedKnowledgeBaseByKnowledgeBaseId.get(knowledgeBaseId);

    if (linkedId) {
      unlinkKnowledgeBaseMutation.mutate({
        sessionId: chatId,
        linkId: linkedId,
      });
      return;
    }

    linkKnowledgeBaseMutation.mutate({
      sessionId: chatId,
      knowledgeBaseId,
    });
  };

  const handleRetry = async (messageId: string) => {
    if (!chatId) {
      return;
    }

    try {
      await retryMessage(chatId, messageId);
    } catch (error) {
      const apiMessage = await resolveApiErrorMessage(error);
      toast.error({
        title: translate('platformChat.retryErrorTitle'),
        description:
          apiMessage !== ''
            ? apiMessage
            : translate('platformChat.retryErrorDescription'),
      });
    }
  };

  const session = sessionQuery.data;
  const isSessionPending = sessionQuery.isPending;
  const listSession = chatId ? getSessionById(chatId) : undefined;

  const latestSeenMessageId = useMemo(() => {
    if (listSession?.mostRecentMessageId) {
      return listSession.mostRecentMessageId;
    }

    for (let index = rawMessages.length - 1; index >= 0; index -= 1) {
      const messageId = rawMessages[index]?.id;
      if (typeof messageId === 'string' && messageId.length > 0) {
        return messageId;
      }
    }

    return null;
  }, [listSession?.mostRecentMessageId, rawMessages]);

  const shouldTrackLastSeen =
    chatId != null &&
    latestSeenMessageId != null &&
    !(
      listSession != null &&
      listSession.participantCount <= 1 &&
      listSession.sharedAs == null
    );

  const pendingLastSeenPersistRef = useRef<{
    sessionId: string;
    messageId: string;
  } | null>(null);

  // Local-only while the chat is open (clears the sidebar "has new" badge).
  useEffect(() => {
    if (!shouldTrackLastSeen || chatId == null || latestSeenMessageId == null) {
      pendingLastSeenPersistRef.current = null;
      return;
    }

    pendingLastSeenPersistRef.current = {
      sessionId: chatId,
      messageId: latestSeenMessageId,
    };
    markPlatformChatSessionSeen(chatId, latestSeenMessageId);
  }, [
    chatId,
    latestSeenMessageId,
    markPlatformChatSessionSeen,
    shouldTrackLastSeen,
  ]);

  // Persist once on leave / chat switch / tab close — not on every new message.
  useEffect(() => {
    if (chatId == null) {
      return;
    }

    const sessionId = chatId;

    const flushLastSeen = () => {
      const pending = pendingLastSeenPersistRef.current;
      if (pending == null || pending.sessionId !== sessionId) {
        return;
      }
      markPlatformChatSessionSeen(pending.sessionId, pending.messageId, {
        persist: true,
      });
    };

    window.addEventListener('pagehide', flushLastSeen);
    return () => {
      window.removeEventListener('pagehide', flushLastSeen);
      flushLastSeen();
    };
  }, [chatId, markPlatformChatSessionSeen]);

  const knowledgeBaseOptions = useMemo(
    () =>
      buildPlatformChatKnowledgeBaseOptions(
        knowledgeDatabasesQuery.data,
        linkedKnowledgeBaseByKnowledgeBaseId,
        locale,
      ),
    [
      knowledgeDatabasesQuery.data,
      linkedKnowledgeBaseByKnowledgeBaseId,
      locale,
    ],
  );

  const documentPreviewKnowledgeBases = useMemo(
    () => buildDocumentPreviewKnowledgeBases(knowledgeDatabasesQuery.data),
    [knowledgeDatabasesQuery.data],
  );

  const fallbackKnowledgeBaseId = useMemo(
    () => resolveFallbackKnowledgeBaseId(linkedKnowledgeBasesQuery.data),
    [linkedKnowledgeBasesQuery.data],
  );

  usePlatformChatScrollEffects({
    chatId,
    displayMessageCount,
    isMessagesLoading: messagesQuery.isLoading,
    checkIsAtBottom,
    scrollToBottom,
    hasInitialScrollRef,
    isLoadingOlderMessagesRef,
  });

  const messageListHeader = (
    <PlatformChatMessageListHeader
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      hasMessages={displayMessageCount > 0}
    />
  );

  if (!chatId) {
    return <Navigate to="/knowledge" replace />;
  }

  if (!session && isSessionPending) {
    return null;
  }

  if (!session) {
    return <Navigate to="/knowledge" replace />;
  }

  return (
    <LiksPage
      className="platform-chat-page"
      content={
        <PlatformChatTranscript
          isMessagesLoading={messagesQuery.isLoading}
          isMessagesError={messagesQuery.isError}
          hasServerMessages={messages.length > 0}
          messages={displayMessages}
          messagesRef={messagesRef}
          messagesScrollbarClassName={messagesScrollbar.className}
          onMessagesScroll={handleMessagesScroll}
          messageListHeader={messageListHeader}
          stickToBottom={stickToBottom}
          currentUserId={user?.id}
          enableRetry={messageCount > 0}
          disableRetry={isAwaitingAssistantReply}
          onRetry={(messageId) => {
            void handleRetry(messageId);
          }}
          isAtBottom={isAtBottom}
          onScrollToBottom={() => scrollToBottom()}
          fallbackKnowledgeBaseId={fallbackKnowledgeBaseId}
          knowledgeBases={documentPreviewKnowledgeBases}
        />
      }
      footer={
        <PlatformChatComposerArea
          key={chatId}
          chatId={chatId}
          isSendingMessage={isAwaitingAssistantReply}
          knowledgeBaseOptions={knowledgeBaseOptions}
          isKnowledgeBaseMenuLoading={
            (knowledgeDatabasesQuery.isLoading &&
              knowledgeDatabasesQuery.data === undefined) ||
            (linkedKnowledgeBasesQuery.isLoading &&
              linkedKnowledgeBasesQuery.data === undefined)
          }
          isLinkedKnowledgeBasesLoading={
            linkedKnowledgeBasesQuery.isLoading &&
            linkedKnowledgeBasesQuery.data === undefined
          }
          hasLinkedKnowledgeBases={
            (linkedKnowledgeBasesQuery.data ?? []).length > 0
          }
          onKnowledgeBaseToggle={handleKnowledgeBaseToggle}
          onSendMessage={handleSendMessage}
        />
      }
      footerAlign="center"
    />
  );
}
